/**
 * Top-level application.
 *
 * Wraps everything in tRPC + TanStack Query providers, then hands the
 * authenticated tree to TanStack Router. Auth gating and the app shell
 * live in `src/routes/__root.tsx`.
 */

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { useState } from "react";

import { ToastProvider } from "./components/ui";
import { buildTrpcClient, trpc } from "./lib/trpc";
import { routeTree } from "./routeTree.gen";

const router = createRouter({
  routeTree,
  // Prefetch on hover/touch-start so navigation feels instant. Mobile
  // browsers fire pointer events ~150–300ms before the click resolves —
  // that head-start is what makes the app feel snappy on phones.
  defaultPreload: "intent",
  // Treat preloaded data as fresh for 10s. Matches the typical interval
  // between intent-event and actual navigation; longer would risk
  // showing stale data, shorter would re-fetch immediately on click.
  defaultPreloadStaleTime: 10_000,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

export default function App() {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
            staleTime: 30_000,
            retry: 1,
          },
        },
      }),
  );
  const [trpcClient] = useState(() => buildTrpcClient());

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <RouterProvider router={router} />
        </ToastProvider>
      </QueryClientProvider>
    </trpc.Provider>
  );
}
